"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.whatsappActionsOpenApiDocs = void 0;
const accessDeniedResponse = {
    description: 'Access denied — super admin required',
    returnParams: {
        type: 'object',
        properties: {
            success: { type: 'boolean', example: false },
            error: { type: 'string' },
            code: { type: 'number', example: 10001 }
        }
    }
};
const badRequestResponse = {
    description: 'Invalid filter parameters',
    returnParams: {
        type: 'object',
        properties: {
            success: { type: 'boolean', example: false },
            error: { type: 'string', description: 'Semicolon-separated list of validation errors', example: 'dateFrom must be in YYYY-MM-DD format; page must be an integer >= 1' },
            code: { type: 'number', example: 10003 }
        }
    }
};
const notFoundResponse = {
    description: 'Resource not found',
    returnParams: {
        type: 'object',
        properties: {
            success: { type: 'boolean', example: false },
            error: { type: 'string' },
            code: { type: 'number', example: 10004 }
        }
    }
};
const internalErrorResponse = {
    description: 'Internal server error',
    returnParams: {
        type: 'object',
        properties: {
            success: { type: 'boolean', example: false },
            error: { type: 'string' },
            code: { type: 'number', example: 10005 }
        }
    }
};
// IAvatar — referenced by ICommsSettingsData.avatar
const AvatarSchema = {
    type: 'object',
    description: 'Avatar associated with the WhatsApp bot settings (IAvatar)',
    properties: {
        id: { type: 'number', description: 'Avatar identifier' },
        name: { type: 'string', description: 'Avatar name' },
    }
};
// ICommsSettingsData
const CommsSettingsDataSchema = {
    type: 'object',
    description: 'WhatsApp bot settings record (ICommsSettingsData)',
    properties: {
        id: { type: 'number', description: 'Settings record identifier' },
        phoneNumber: { type: 'string', description: 'WhatsApp phone number for the bot', example: '+48123456789' },
        title: { type: 'string', description: 'Optional title for the bot settings' },
        reportEmail: { type: 'string', description: 'Comma-separated list of email addresses to receive bot reports. Accepts a comma-separated string ("a@x.com,b@x.com") or an array — stored and returned as a single comma-separated string.', example: 'reports@example.com,admin@example.com' },
        language: { type: 'string', description: 'Language code used by the bot', example: 'pl' },
        communicationType: { type: 'string', description: 'Communication type for the bot', example: 'WA', enum: ['WA', 'TELE'] },
        botResponseCondition: { type: 'string', description: 'Condition that determines when the bot should respond - empty disables responses', example: 'always' },
        avatar: AvatarSchema
    },
    required: ['phoneNumber']
};
// ICommsSettingsResponse
const CommsSettingsResponseSchema = {
    type: 'object',
    description: 'Standard WhatsApp settings response (ICommsSettingsResponse)',
    properties: {
        success: { type: 'boolean', example: true },
        data: CommsSettingsDataSchema,
        error: { type: 'string' }
    }
};
const CommsSettingsBulkResponseSchema = {
    type: 'object',
    description: 'Standard WhatsApp settings bulk response (ICommsSettingsBulkResponse). `errors` is a map keyed by the offending phone number, with an array of error codes (e.g. INVALID_PHONE_NUMBER, DUPLICATE_PHONE_NUMBER) per phone.',
    properties: {
        success: { type: 'boolean', example: true },
        data: {
            type: 'array',
            items: CommsSettingsDataSchema
        },
        errors: {
            type: 'object',
            description: 'Map of phone number -> list of error codes. Empty object when no errors. success false when populated',
            additionalProperties: {
                type: 'array',
                items: {
                    type: 'string',
                    enum: ['INVALID_PHONE_NUMBER', 'DUPLICATE_PHONE_NUMBER']
                }
            },
            example: {
                '48123456789': ['INVALID_PHONE_NUMBER'],
                '48987654321': ['DUPLICATE_PHONE_NUMBER']
            }
        }
    }
};
// ICommsSettingsListResponse
const CommsSettingsListResponseSchema = {
    type: 'object',
    description: 'List of WhatsApp settings (ICommsSettingsListResponse)',
    properties: {
        success: { type: 'boolean', example: true },
        data: {
            type: 'array',
            items: CommsSettingsDataSchema
        },
        error: { type: 'string' }
    }
};
// IWhatsAppLogoutResponse
const WhatsAppLogoutResponseSchema = {
    type: 'object',
    description: 'WhatsApp logout response (IWhatsAppLogoutResponse)',
    properties: {
        success: { type: 'boolean', example: true },
        data: {
            type: 'object',
            properties: {
                message: { type: 'string', description: 'Human-readable logout message' }
            }
        },
        error: { type: 'string' }
    }
};
// IWhatsAppStartAgentResponse
const WhatsAppStartAgentResponseSchema = {
    type: 'object',
    description: 'WhatsApp start-agent response (IWhatsAppStartAgentResponse)',
    properties: {
        success: { type: 'boolean', example: true },
        data: {
            type: 'object',
            properties: {
                message: { type: 'string', description: 'Human-readable status message' },
                phoneNumber: { type: 'string', description: 'Phone number the agent was started for', example: '+48123456789' }
            }
        },
        error: { type: 'string' }
    }
};
// ICommsGroupData
const WhatsAppGroupDataSchema = {
    type: 'object',
    description: 'WhatsApp group record (ICommsGroupData)',
    properties: {
        id: { type: 'string', description: 'Group identifier' },
        phoneNumber: { type: 'string', description: 'Phone number owning the group', example: '+48123456789' },
        groupWaId: { type: 'string', description: 'WhatsApp group ID' },
        groupName: { type: 'string', description: 'Group display name' },
        tracking: { type: 'boolean', description: 'Whether tracking is enabled for this group' },
        announce: { type: 'boolean', description: 'Whether only admins can post (announce mode)' },
        isOwned: { type: 'boolean', description: 'Whether the bot owns this group' },
        botInstruction: { type: 'string', description: 'Custom bot instruction for this group' },
        invitePhoneNumbers: {
            type: 'array',
            description: 'Phone numbers invited to the group',
            items: { type: 'string' }
        },
        remoteAccess: { type: 'boolean', description: 'Whether remote access is enabled' },
        accessPassword: { type: 'string', description: 'Password for remote access' },
        groupReactionStatus: { type: 'string', description: 'Reaction status of the group' },
        groupStatusComment: { type: 'string', description: 'Status comment of the group' }
    }
};
// ICommsGroupPayload
const WhatsAppGroupPayloadSchema = {
    type: 'object',
    description: 'WhatsApp group create/update payload (ICommsGroupPayload)',
    properties: {
        name: { type: 'string', description: 'Group name', example: 'Phone 1' },
        invitePhoneNumbers: {
            type: 'array',
            description: 'Phone numbers to invite to the group',
            items: { type: 'string' }
        },
        botInstruction: { type: 'string', description: 'Custom bot instruction for this group' },
        remoteAccess: { type: 'boolean', description: 'Whether remote access is enabled' },
        accessPassword: { type: 'string', description: 'Password for remote access' }
    },
    required: ['name']
};
// ICommsGroupResponse
const WhatsAppGroupResponseSchema = {
    type: 'object',
    description: 'Standard WhatsApp group response (ICommsGroupResponse)',
    properties: {
        success: { type: 'boolean', example: true },
        data: WhatsAppGroupDataSchema,
        error: { type: 'string' }
    }
};
// ICommsGroupListResponse
const WhatsAppGroupListResponseSchema = {
    type: 'object',
    description: 'List of WhatsApp groups (ICommsGroupListResponse)',
    properties: {
        success: { type: 'boolean', example: true },
        data: {
            type: 'array',
            items: WhatsAppGroupDataSchema
        },
        error: { type: 'string' }
    }
};
// ICommsGroupMessageData
const WhatsAppGroupMessageDataSchema = {
    type: 'object',
    description: 'WhatsApp group/direct message (ICommsGroupMessageData)',
    properties: {
        id: { type: 'string', description: 'Message identifier' },
        groupWaId: { type: 'string', description: 'WhatsApp group ID (for group messages)' },
        phoneNumber: { type: 'string', description: 'Phone number owning the conversation' },
        memberId: { type: 'string', description: 'Member identifier' },
        memberPhone: { type: 'string', description: 'Member phone number' },
        senderName: { type: 'string', description: 'Sender display name' },
        messageContent: { type: 'string', description: 'Message body' },
        waId: { type: 'string', description: 'WhatsApp message ID' },
        timestamp: { type: 'string', format: 'date-time', description: 'Message timestamp' },
        mediaType: { type: 'string', description: 'Type of media attached to the message' },
        mediaName: { type: 'string', description: 'Name of the attached media file' },
        isFromAgent: { type: 'boolean', description: 'Whether the message was sent by the agent' },
        recipientId: { type: 'string', description: 'Recipient identifier' },
        recipientName: { type: 'string', description: 'Recipient display name' }
    },
    required: ['messageContent']
};
// ICommsGroupMessageListResponse
const WhatsAppGroupMessageListResponseSchema = {
    type: 'object',
    description: 'List of WhatsApp group/direct messages (ICommsGroupMessageListResponse)',
    properties: {
        success: { type: 'boolean', example: true },
        data: {
            type: 'array',
            items: WhatsAppGroupMessageDataSchema
        },
        error: { type: 'string' }
    }
};
// ICommsGroupMemberData
const WhatsAppGroupMemberDataSchema = {
    type: 'object',
    description: 'WhatsApp group member (ICommsGroupMemberData)',
    properties: {
        id: { type: 'string', description: 'Member record identifier' },
        memberId: { type: 'string', description: 'WhatsApp member identifier' },
        memberPhone: { type: 'string', description: 'Member phone number' },
        displayName: { type: 'string', description: 'Member display name' },
        isAdmin: { type: 'boolean', description: 'Whether the member is an admin' },
        isSuperAdmin: { type: 'boolean', description: 'Whether the member is a super admin' }
    },
    required: ['memberId']
};
// ICommsGroupMemberListResponse
const WhatsAppGroupMemberListResponseSchema = {
    type: 'object',
    description: 'List of WhatsApp group members (ICommsGroupMemberListResponse)',
    properties: {
        success: { type: 'boolean', example: true },
        data: {
            type: 'array',
            items: WhatsAppGroupMemberDataSchema
        },
        error: { type: 'string' }
    }
};
// ICommsGroupSummaryData
const WhatsAppGroupSummaryDataSchema = {
    type: 'object',
    description: 'WhatsApp group/direct-message summary (ICommsGroupSummaryData)',
    properties: {
        id: { type: 'number', description: 'Summary identifier' },
        dateKey: { type: 'string', description: 'Date key the summary covers (YYYY-MM-DD)' },
        dateFrom: { type: 'string', format: 'date-time', description: 'Inclusive start of the summary window' },
        dateTo: { type: 'string', format: 'date-time', description: 'Inclusive end of the summary window' },
        summaryText: { type: 'string', description: 'Generated summary text' }
    },
    required: ['dateKey', 'summaryText']
};
// ICommsGroupSummaryListResponse
const WhatsAppGroupSummaryListResponseSchema = {
    type: 'object',
    description: 'List of WhatsApp group/direct-message summaries (ICommsGroupSummaryListResponse)',
    properties: {
        success: { type: 'boolean', example: true },
        data: {
            type: 'array',
            items: WhatsAppGroupSummaryDataSchema
        },
        error: { type: 'string' }
    }
};
// IWhatsAppTrackLogData
const WhatsAppTrackLogDataSchema = {
    type: 'object',
    description: 'WhatsApp track log entry (IWhatsAppTrackLogData)',
    properties: {
        id: { type: 'string', description: 'Track log identifier' },
        groupId: { type: 'string', description: 'Group identifier (for group track logs)' },
        groupWaId: { type: 'string', description: 'WhatsApp group ID' },
        groupName: { type: 'string', description: 'Group display name' },
        phoneNumber: { type: 'string', description: 'Phone number owning the conversation' },
        authorName: { type: 'string', description: 'Author display name' },
        authorLid: { type: 'string', description: 'Author WhatsApp LID' },
        logType: { type: 'string', description: 'Type of the track log entry' },
        isHandled: { type: 'boolean', description: 'Whether the alert has been handled' },
        handledDate: { type: 'string', format: 'date-time', description: 'When the alert was handled' },
        title: { type: 'string', description: 'Track log title' },
        shortSummary: { type: 'string', description: 'Short summary of the track log' },
        timestamp: { type: 'string', format: 'date-time', description: 'Track log timestamp' }
    }
};
// IWhatsAppTrackLogListResponse
const WhatsAppTrackLogListResponseSchema = {
    type: 'object',
    description: 'List of WhatsApp track logs (IWhatsAppTrackLogListResponse)',
    properties: {
        success: { type: 'boolean', example: true },
        data: {
            type: 'array',
            items: WhatsAppTrackLogDataSchema
        },
        error: { type: 'string' }
    }
};
// IWhatsAppDirectMessageContact
const WhatsAppDirectMessageContactSchema = {
    type: 'object',
    description: 'Direct-message contact summary (IWhatsAppDirectMessageContact)',
    properties: {
        authorLid: { type: 'string', description: 'Author WhatsApp LID' },
        authorName: { type: 'string', description: 'Author display name' },
        messageCount: { type: 'integer', description: 'Number of messages exchanged with this contact' },
        lastMessage: { type: 'string', description: 'Last message preview' },
        lastTimestamp: { type: 'string', format: 'date-time', description: 'Timestamp of the last message' }
    },
    required: ['authorLid', 'messageCount']
};
// IWhatsAppDirectMessagesResponse
const WhatsAppDirectMessagesResponseSchema = {
    type: 'object',
    description: 'List of direct-message contacts (IWhatsAppDirectMessagesResponse)',
    properties: {
        success: { type: 'boolean', example: true },
        data: {
            type: 'array',
            items: WhatsAppDirectMessageContactSchema
        },
        error: { type: 'string' }
    }
};
// IWhatsAppDirectMessageTrackLogsResponse
const WhatsAppDirectMessageTrackLogsResponseSchema = {
    type: 'object',
    description: 'List of direct-message track logs (IWhatsAppDirectMessageTrackLogsResponse)',
    properties: {
        success: { type: 'boolean', example: true },
        data: {
            type: 'array',
            items: WhatsAppTrackLogDataSchema
        },
        error: { type: 'string' }
    }
};
// Generic success response used by simple boolean endpoints
const SuccessResponseSchema = {
    type: 'object',
    description: 'Generic success/error response',
    properties: {
        success: { type: 'boolean', example: true },
        error: { type: 'string' }
    }
};
// Public auth response
const PublicAuthResponseSchema = {
    type: 'object',
    description: 'Public group auth response',
    properties: {
        auth: { type: 'boolean', example: true },
        token: { type: 'string', description: 'Auth token when authentication succeeds' }
    }
};
const SettingsPayload = {
    title: { type: 'string', description: 'Bot name', example: 'My WhatsApp Bot', required: true },
    phoneNumber: { type: 'string', description: 'WhatsApp phone number for the bot', example: '+48123456789', required: true },
    reportEmail: { type: 'string', description: 'Comma-separated list of email addresses to receive bot reports. Accepts a comma-separated string ("a@x.com,b@x.com") or an array — stored and returned as a single comma-separated string.', example: 'reports@example.com,admin@example.com' },
    language: { type: 'string', description: 'Language code used by the bot', example: 'pl' },
    botResponseCondition: { type: 'string', description: 'Condition that determines when the bot should respond - empty disables responses', example: 'always' },
    avatarId: { type: 'number', description: 'ID of the avatar associated with the bot', example: 1 },
    communicationType: { type: 'enum', description: 'Communication type for the bot', example: 'WA', enum: ['WA', 'TELE'] },
};
exports.whatsappActionsOpenApiDocs = {
    // ---------- Settings ----------
    'whatsapp.logout': {
        description: 'Logs out the WhatsApp bot for the given phone number',
        payload: {
            body: {
                type: 'object',
                properties: {
                    phoneNumber: { type: 'string', description: 'WhatsApp phone number to log out', example: '+48123456789', required: true }
                },
                required: ['phoneNumber']
            }
        },
        responses: {
            200: { description: 'Logout successful', returnParams: WhatsAppLogoutResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.settings.list': {
        description: 'Lists WhatsApp bot settings, optionally filtered by phone numbers',
        payload: {
            query: {
                type: 'object',
                properties: {
                    phoneNumbers: {
                        type: 'string',
                        description: 'Comma-separated list of phone numbers to filter by (e.g. "+48123,+48999")',
                        example: '+48123456789,+48987654321'
                    }
                }
            }
        },
        responses: {
            200: { description: 'Settings list retrieved successfully', returnParams: CommsSettingsListResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.settings.get': {
        description: 'Returns the current user\'s WhatsApp bot settings',
        responses: {
            200: { description: 'Settings retrieved successfully', returnParams: CommsSettingsResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.settings.getById': {
        description: 'Returns a WhatsApp bot settings record by ID',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Settings record identifier', required: true }
                },
                required: ['id']
            }
        },
        responses: {
            200: { description: 'Settings retrieved successfully', returnParams: CommsSettingsResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.settings.create': {
        description: 'Creates a new WhatsApp bot number settings record',
        payload: {
            body: {
                type: 'object',
                properties: SettingsPayload,
                required: ['phoneNumber']
            }
        },
        responses: {
            200: { description: 'Settings created successfully', returnParams: CommsSettingsResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.settings.bulk-update': {
        description: 'Updates multiple WhatsApp bot number settings records',
        payload: {
            body: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: SettingsPayload,
                    required: ['phoneNumber']
                }
            }
        },
        responses: {
            200: { description: 'Settings updated successfully', returnParams: CommsSettingsBulkResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.settings.save': {
        description: 'Saves (creates or replaces) the current user\'s WhatsApp bot settings',
        payload: {
            body: {
                type: 'object',
                properties: {
                    phoneNumber: { type: 'string', description: 'WhatsApp phone number for the bot', example: '+48123456789', required: true },
                    reportEmail: { type: 'string', description: 'Comma-separated list of email addresses to receive bot reports. Accepts a comma-separated string ("a@x.com,b@x.com") or an array — stored and returned as a single comma-separated string.', example: 'reports@example.com,admin@example.com' },
                    language: { type: 'string', description: 'Language code used by the bot', example: 'pl' },
                    botResponseCondition: { type: 'string', description: 'Condition that determines when the bot should respond - empty disables responses', example: 'always' },
                    avatarId: { type: 'number', description: 'ID of the avatar associated with the bot', example: 1 }
                },
                required: ['phoneNumber']
            }
        },
        responses: {
            200: { description: 'Settings saved successfully', returnParams: CommsSettingsResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.settings.update': {
        description: 'Updates an existing WhatsApp bot settings record',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Settings record identifier', required: true }
                },
                required: ['id']
            },
            body: {
                type: 'object',
                properties: {
                    phoneNumber: { type: 'string', description: 'WhatsApp phone number for the bot', example: '+48123456789' },
                    reportEmail: { type: 'string', description: 'Comma-separated list of email addresses to receive bot reports. Accepts a comma-separated string ("a@x.com,b@x.com") or an array — stored and returned as a single comma-separated string.', example: 'reports@example.com,admin@example.com' },
                    language: { type: 'string', description: 'Language code used by the bot', example: 'pl' },
                    botResponseCondition: { type: 'string', description: 'Condition that determines when the bot should respond - empty disables responses', example: 'always' },
                    avatarId: { type: 'number', description: 'ID of the avatar associated with the bot', example: 1 }
                }
            }
        },
        responses: {
            200: { description: 'Settings updated successfully', returnParams: CommsSettingsResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.settings.delete': {
        description: 'Deletes a WhatsApp bot settings record',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Settings record identifier', required: true }
                },
                required: ['id']
            }
        },
        responses: {
            200: { description: 'Settings deleted successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.startAgent': {
        description: 'Starts the WhatsApp agent for the current user',
        responses: {
            200: { description: 'Agent started successfully', returnParams: WhatsAppStartAgentResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    // ---------- Groups ----------
    'whatsapp.groups.list': {
        description: 'Lists WhatsApp groups for the given settings ID, optionally filtered by phone numbers',
        payload: {
            params: {
                type: 'object',
                properties: {
                    settingsId: { type: 'string', description: 'Settings record identifier', required: true }
                },
                required: ['settingsId']
            },
            query: {
                type: 'object',
                properties: {
                    phoneNumbers: {
                        type: 'string',
                        description: 'Comma-separated list of phone numbers to filter by',
                        example: '+48123456789,+48987654321'
                    }
                }
            }
        },
        responses: {
            200: { description: 'Groups retrieved successfully', returnParams: WhatsAppGroupListResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.get': {
        description: 'Returns a single WhatsApp group by ID',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            }
        },
        responses: {
            200: { description: 'Group retrieved successfully', returnParams: WhatsAppGroupResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.setTracking': {
        description: 'Toggles tracking for a WhatsApp group',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            },
            body: {
                type: 'object',
                properties: {
                    tracking: { type: 'boolean', description: 'Whether tracking should be enabled', required: true }
                },
                required: ['tracking']
            }
        },
        responses: {
            200: { description: 'Tracking updated successfully', returnParams: WhatsAppGroupResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.messages': {
        description: 'Lists messages for a WhatsApp group within an optional date range',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            },
            query: {
                type: 'object',
                properties: {
                    dateFrom: { type: 'string', format: 'date', description: 'Inclusive start date (YYYY-MM-DD)' },
                    dateTo: { type: 'string', format: 'date', description: 'Inclusive end date (YYYY-MM-DD)' },
                    limit: { type: 'integer', description: 'Maximum number of messages to return', minimum: 1 }
                }
            }
        },
        responses: {
            200: { description: 'Messages retrieved successfully', returnParams: WhatsAppGroupMessageListResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.create': {
        description: 'Creates a new WhatsApp group linked to the given WhatsApp settings record',
        payload: {
            params: {
                type: 'object',
                properties: {
                    settingsId: { type: 'string', description: 'Settings record identifier the new group will be linked to', required: true }
                },
                required: ['settingsId']
            },
            body: WhatsAppGroupPayloadSchema
        },
        responses: {
            200: { description: 'Group created successfully', returnParams: WhatsAppGroupResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.update': {
        description: 'Updates an existing WhatsApp group',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            },
            body: WhatsAppGroupPayloadSchema
        },
        responses: {
            200: { description: 'Group updated successfully', returnParams: WhatsAppGroupResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.members': {
        description: 'Lists members of a WhatsApp group',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            }
        },
        responses: {
            200: { description: 'Members retrieved successfully', returnParams: WhatsAppGroupMemberListResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.syncMembers': {
        description: 'Synchronizes members of a WhatsApp group with the WhatsApp server',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            }
        },
        responses: {
            200: { description: 'Members synchronized successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.promoteMember': {
        description: 'Promotes a member of a WhatsApp group to admin',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            },
            body: {
                type: 'object',
                properties: {
                    memberId: { type: 'string', description: 'Member identifier to promote', required: true }
                },
                required: ['memberId']
            }
        },
        responses: {
            200: { description: 'Member promoted successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.setAnnounce': {
        description: 'Sets the announce (admins-only) mode for a WhatsApp group',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            },
            body: {
                type: 'object',
                properties: {
                    adminsOnly: { type: 'boolean', description: 'Whether only admins should be able to post', required: true }
                },
                required: ['adminsOnly']
            }
        },
        responses: {
            200: { description: 'Announce mode updated successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.summarize': {
        description: 'Generates a summary for a WhatsApp group, optionally for a specific date',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            },
            body: {
                type: 'object',
                properties: {
                    date: { type: 'string', format: 'date', description: 'Date to summarize (YYYY-MM-DD)' }
                }
            }
        },
        responses: {
            200: { description: 'Summary generated successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.addParticipants': {
        description: 'Adds participants to a WhatsApp group',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            },
            body: {
                type: 'object',
                properties: {
                    participants: {
                        type: 'array',
                        description: 'Phone numbers to add to the group',
                        items: { type: 'string' },
                        required: true
                    }
                },
                required: ['participants']
            }
        },
        responses: {
            200: { description: 'Participants added successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.removeMember': {
        description: 'Removes a member from a WhatsApp group',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            },
            body: {
                type: 'object',
                properties: {
                    memberId: { type: 'string', description: 'Member identifier to remove', required: true }
                },
                required: ['memberId']
            }
        },
        responses: {
            200: { description: 'Member removed successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.summaries': {
        description: 'Lists summaries for a WhatsApp group',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            }
        },
        responses: {
            200: { description: 'Summaries retrieved successfully', returnParams: WhatsAppGroupSummaryListResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.syncMessages': {
        description: 'Synchronizes messages of a WhatsApp group with the WhatsApp server',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            }
        },
        responses: {
            200: { description: 'Messages synchronized successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.sendMessage': {
        description: 'Sends a message to a WhatsApp group',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            },
            body: {
                type: 'object',
                properties: {
                    message: { type: 'string', description: 'Message body to send', required: true }
                },
                required: ['message']
            }
        },
        responses: {
            200: { description: 'Message sent successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.downloadMedia': {
        description: 'Downloads media attached to a WhatsApp message',
        payload: {
            params: {
                type: 'object',
                properties: {
                    messageId: { type: 'string', description: 'Message identifier whose media should be downloaded', required: true }
                },
                required: ['messageId']
            }
        },
        responses: {
            200: {
                description: 'Media file stream',
                returnParams: {
                    type: 'string',
                    format: 'binary',
                    description: 'Binary media file stream'
                }
            },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.view': {
        description: 'Public endpoint that renders the comms gate HTML page',
        payload: {
            query: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Optional group identifier to scope the gate to' },
                    phoneNumbers: {
                        type: 'string',
                        description: 'Comma-separated list of phone numbers to scope the gate to',
                        example: '+48123456789,+48987654321'
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'HTML page rendered successfully',
                returnParams: {
                    type: 'string',
                    description: 'HTML markup of the comms gate'
                }
            },
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.public_auth': {
        description: 'Public endpoint that authenticates a user against a WhatsApp group password',
        payload: {
            body: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true },
                    password: { type: 'string', description: 'Group access password', required: true }
                },
                required: ['id', 'password']
            }
        },
        responses: {
            200: { description: 'Authentication result', returnParams: PublicAuthResponseSchema },
            500: internalErrorResponse
        }
    },
    'whatsapp.groups.trackLogs': {
        description: 'Lists track logs for a WhatsApp group',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Group identifier', required: true }
                },
                required: ['id']
            }
        },
        responses: {
            200: { description: 'Track logs retrieved successfully', returnParams: WhatsAppTrackLogListResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.trackLogs.handleAlert': {
        description: 'Marks a track log alert as handled or unhandled',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'string', description: 'Track log identifier', required: true }
                },
                required: ['id']
            },
            body: {
                type: 'object',
                properties: {
                    value: { type: 'boolean', description: 'Whether the alert should be marked as handled', required: true }
                },
                required: ['value']
            }
        },
        responses: {
            200: { description: 'Alert state updated successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            404: notFoundResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.trackLogs.all': {
        description: 'Lists all track logs for the given settings ID, optionally filtered by direct-message flag and phone numbers',
        payload: {
            params: {
                type: 'object',
                properties: {
                    settingsId: { type: 'string', description: 'Settings record identifier', required: true }
                },
                required: ['settingsId']
            },
            query: {
                type: 'object',
                properties: {
                    dm: { type: 'boolean', description: 'When true, returns direct-message track logs only', default: false },
                    phoneNumbers: {
                        type: 'string',
                        description: 'Comma-separated list of phone numbers to filter by',
                        example: '+48123456789,+48987654321'
                    }
                }
            }
        },
        responses: {
            200: { description: 'Track logs retrieved successfully', returnParams: WhatsAppTrackLogListResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    // ---------- Direct messages ----------
    'whatsapp.directMessages.list': {
        description: 'Lists direct-message contacts for the given settings ID, optionally filtered by phone numbers',
        payload: {
            params: {
                type: 'object',
                properties: {
                    settingsId: { type: 'string', description: 'Settings record identifier', required: true }
                },
                required: ['settingsId']
            },
            query: {
                type: 'object',
                properties: {
                    phoneNumbers: {
                        type: 'string',
                        description: 'Comma-separated list of phone numbers to filter by',
                        example: '+48123456789,+48987654321'
                    }
                }
            }
        },
        responses: {
            200: { description: 'Direct-message contacts retrieved successfully', returnParams: WhatsAppDirectMessagesResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.directMessages.trackLogs': {
        description: 'Lists track logs for a direct-message conversation',
        payload: {
            params: {
                type: 'object',
                properties: {
                    settingsId: { type: 'string', description: 'Settings record identifier', required: true },
                    authorLid: { type: 'string', description: 'Author WhatsApp LID (URL-encoded)', required: true }
                },
                required: ['settingsId', 'authorLid']
            },
            query: {
                type: 'object',
                properties: {
                    phoneNumbers: {
                        type: 'string',
                        description: 'Comma-separated list of phone numbers to filter by',
                        example: '+48123456789,+48987654321'
                    }
                }
            }
        },
        responses: {
            200: { description: 'Direct-message track logs retrieved successfully', returnParams: WhatsAppDirectMessageTrackLogsResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.directMessages.messages': {
        description: 'Lists messages for a direct-message conversation within an optional date range',
        payload: {
            params: {
                type: 'object',
                properties: {
                    settingsId: { type: 'string', description: 'Settings record identifier', required: true },
                    authorLid: { type: 'string', description: 'Author WhatsApp LID (URL-encoded)', required: true }
                },
                required: ['settingsId', 'authorLid']
            },
            query: {
                type: 'object',
                properties: {
                    dateFrom: { type: 'string', format: 'date', description: 'Inclusive start date (YYYY-MM-DD)' },
                    dateTo: { type: 'string', format: 'date', description: 'Inclusive end date (YYYY-MM-DD)' },
                    phoneNumbers: {
                        type: 'string',
                        description: 'Comma-separated list of phone numbers to filter by',
                        example: '+48123456789,+48987654321'
                    }
                }
            }
        },
        responses: {
            200: { description: 'Direct messages retrieved successfully', returnParams: WhatsAppGroupMessageListResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.directMessages.summaries': {
        description: 'Lists summaries for a direct-message conversation',
        payload: {
            params: {
                type: 'object',
                properties: {
                    settingsId: { type: 'string', description: 'Settings record identifier', required: true },
                    authorLid: { type: 'string', description: 'Author WhatsApp LID (URL-encoded)', required: true }
                },
                required: ['settingsId', 'authorLid']
            },
            query: {
                type: 'object',
                properties: {
                    phoneNumbers: {
                        type: 'string',
                        description: 'Comma-separated list of phone numbers to filter by',
                        example: '+48123456789,+48987654321'
                    }
                }
            }
        },
        responses: {
            200: { description: 'Direct-message summaries retrieved successfully', returnParams: WhatsAppGroupSummaryListResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.directMessages.summarize': {
        description: 'Generates a summary for a direct-message conversation, optionally for a date range',
        payload: {
            params: {
                type: 'object',
                properties: {
                    settingsId: { type: 'string', description: 'Settings record identifier', required: true },
                    authorLid: { type: 'string', description: 'Author WhatsApp LID (URL-encoded)', required: true }
                },
                required: ['settingsId', 'authorLid']
            },
            body: {
                type: 'object',
                properties: {
                    date: { type: 'string', format: 'date', description: 'Start date to summarize (YYYY-MM-DD)' },
                    dateTo: { type: 'string', format: 'date', description: 'End date to summarize (YYYY-MM-DD)' },
                    phoneNumbers: {
                        type: 'array',
                        description: 'Phone numbers to scope the summary to',
                        items: { type: 'string' }
                    }
                }
            }
        },
        responses: {
            200: { description: 'Summary generated successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.directMessages.syncMessages': {
        description: 'Synchronizes messages of a direct-message conversation with the WhatsApp server',
        payload: {
            params: {
                type: 'object',
                properties: {
                    settingsId: { type: 'string', description: 'Settings record identifier', required: true },
                    authorLid: { type: 'string', description: 'Author WhatsApp LID (URL-encoded)', required: true }
                },
                required: ['settingsId', 'authorLid']
            },
            query: {
                type: 'object',
                properties: {
                    phoneNumbers: {
                        type: 'string',
                        description: 'Comma-separated list of phone numbers to filter by',
                        example: '+48123456789,+48987654321'
                    }
                }
            }
        },
        responses: {
            200: { description: 'Messages synchronized successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    },
    'whatsapp.directMessages.sendMessage': {
        description: 'Sends a message to a direct-message conversation',
        payload: {
            params: {
                type: 'object',
                properties: {
                    settingsId: { type: 'string', description: 'Settings record identifier', required: true },
                    authorLid: { type: 'string', description: 'Author WhatsApp LID (URL-encoded)', required: true }
                },
                required: ['settingsId', 'authorLid']
            },
            body: {
                type: 'object',
                properties: {
                    message: { type: 'string', description: 'Message body to send', required: true }
                },
                required: ['message']
            }
        },
        responses: {
            200: { description: 'Message sent successfully', returnParams: SuccessResponseSchema },
            403: accessDeniedResponse,
            400: badRequestResponse,
            500: internalErrorResponse
        }
    }
};
